/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from "react";
import { useSelector } from "react-redux";

import ErrorComponent from "web/Layout/Common/ErrorComponent";
import { SkeletonHeaderMenu } from "web/Layout/Common/SkeletonComponents";
import { getLanguageCode } from "web/Layout/Translations/LanguageWrapper";

import type { ICategory } from "web/types/Category";
import { MenuData } from "web/types/Menu";
import type { IStoreConfig } from "web/types/StoreConfig";

import { useAppContext } from "web/context/app";
import { useGetEmployeeDetailsQuery } from "web/features/employee/employeeApiSlice";
import useDataCachedCategoryCounts from "web/features/useDataCached/useDataCachedCategoryCounts";

import MainMenu from "./mainMenu";
import handleMenuByItemType from "./utils/handleMenuByItemType";

export interface IMainMenuContainerWithCategoryProductCountProps {
  menu: MenuData;
  ids: number[];
  storeConfig: IStoreConfig;
  categories: ICategory[];
}

const MainMenuContainerWithCategoryProductCount: FC<
  IMainMenuContainerWithCategoryProductCountProps
> = ({ menu, ids, storeConfig, categories }) => {
  const { isMobile } = useAppContext();
  const currentLanguage = getLanguageCode();
  const {
    msi_menu: msMenu,
    msi_url: msiUrl,
    msi_url_en: msiUrlEn,
    mybenefit_active_url: MBActiveUrl,
    mybenefit_active_url_en: MBActiveUrlEn,
    mybenefit_active_menu: MBActiveMenu,
    mybenefit_active_menu_en: MBActiveMenuEn,
  } = storeConfig;

  const {
    loading,
    data: productCounts,
    error,
  } = useDataCachedCategoryCounts({
    ids,
  });

  const { data: employeeDetails } = useGetEmployeeDetailsQuery();
  const {
    msiCartsEnabled: msiCartsEnabledSelector,
    mbActiveEnabled: mbActiveEnabledSelector,
    functionalities,
  } = employeeDetails || {};

  const { hasEnabledAnySubscriptions, config: subscriptionConfig } =
    useSelector((state) => state.subscriptionQuote);

  switch (true) {
    case !!loading: {
      return <SkeletonHeaderMenu />;
    }
    case !!error: {
      return <ErrorComponent />;
    }
    default: {
      const menuProcessed = handleMenuByItemType({
        menu,
        categories,
        productCounts: productCounts!,
        functionalities,
        mbActiveEnabledSelector,
        MBActiveUrl,
        MBActiveUrlEn,
        MBActiveMenu,
        MBActiveMenuEn,
        currentLanguage,
        msiUrl,
        msiUrlEn,
        msiCartsEnabledSelector,
        msMenu,
        hasEnabledAnySubscriptions,
        subscriptionConfig,
      }) as MenuData;

      return <>{isMobile ? null : <MainMenu menu={menuProcessed} />}</>;
    }
  }
};

export default MainMenuContainerWithCategoryProductCount;
